// ============================================================
// WHERE A TOKEN'S LOOKS ARE DRAWN (plan body-map M1): its foot and head on
// the screen, its base ring's screen radii, and its head's. Ground looks are
// drawn in the GROUND FRAME - x across, y into the map, in screen pixels at
// the ring's scale, squashed by the camera's pitch - so a ring of tiles
// turning there turns on the ground, and a circle there is the ring's
// ellipse. The design's metres become ring units (engine/display/looks rings).
// ============================================================

import type { CSSProperties } from 'react';
import type { ScreenXY } from '../../battlefieldTokens/markerGeometry';
import { rings } from '../../../engine/display/looks';

/** An inline style that sets the looks' CSS variables. */
export type Vars = CSSProperties & Record<`--${string}`, string | number>;
export const TAU = Math.PI * 2;

export interface LookPlace {
  foot: ScreenXY;
  head: ScreenXY;
  /** The base ring's screen radii (rx across, ry deep). */
  rx: number;
  ry: number;
  /** The head's screen radius. */
  hr: number;
}

/** The design's head height (m): its heights are read against it. */
const HEAD_M = 0.9;

/** How the camera's pitch squashes the ground: the ring's depth over its width. */
export const squash = (p: Pick<LookPlace, 'rx' | 'ry'>) => p.ry / Math.max(p.rx, 0.001);

/** The ground frame's transform: the foot as origin, depth squashed by the pitch. */
export const groundFrame = (p: LookPlace) =>
  `translate(${p.foot.x.toFixed(1)} ${p.foot.y.toFixed(1)}) scale(1 ${squash(p).toFixed(3)})`;

/** A ground distance in metres, as ground-frame pixels. */
export const metres = (p: Pick<LookPlace, 'rx'>, m: number) => rings(m) * p.rx;

/** A height in metres, as screen pixels up from the foot (the head stands at the design's 0.9 m). */
export const height = (p: LookPlace, m: number) => (m / HEAD_M) * Math.max(p.foot.y - p.head.y, p.hr);

/** The head, relative to the foot. */
export const headFromFoot = (p: LookPlace) => ({ x: p.head.x - p.foot.x, y: p.head.y - p.foot.y });

/** A unit-circle point at radius `r` px and angle `a`. */
export const onCircle = (r: number, a: number): [number, number] => [r * Math.cos(a), r * Math.sin(a)];

export const px = (n: number) => Number(n.toFixed(1));
